import React from "react";
import {toDate} from "date-fns";

import {styled, Paragraph, DATE_FORMAT} from "@reside/ui";

import {
  AdmissionId,
  CellFooter,
  CellIdentityBlock,
  CellLastUpdatePrefix,
  CellLastUpdateRow,
  CellLastUpdateTime,
  CellResidentNameLink,
  DashboardCell,
  Left,
  Right,
} from "../../../../dashboard-cell";
import {WorkflowRibbon} from "../../../../dashboard-cell/WorkflowRibbon";
import {EditPreflightFooterButton} from "../EditPreflightDropdownItem";
import {AdmissionFromSearch} from "../../../../../services/AdmissionsService";
import {RelativeDateTime} from "../../../../relative-date-time";
import {readableAdmissionStatus} from "../../../../admission-status-tag";
import {getAdmissionResidentName, getResidentTestId} from "../../../helper";
import {SmallTag} from "../../days-of-admission-tag";
import {AdmissionChecklist} from "../admission-checklist";
import {PccReadmittedActions} from "../PccReadmittedActions";
import {PccAdmittedActions} from "../PccAdmittedActions";
import {StarAdmissionIcon} from "../../../../star-icon/StarAdmissionIcon";

type Props = Readonly<{
  admission: AdmissionFromSearch;
}>;

export const AdmissionPreflightCell = (props: Props) => {
  const {
    updated,
    created,
    pcc: {readmittedDate, admittedDate} = {},
  } = props.admission;

  const isNewPreflight = updated === created;

  const isPccAdmitted = admittedDate && isNewPreflight;

  const isPccReadmitted = readmittedDate && isNewPreflight;

  return isPccAdmitted ? (
    <PccAdmittedCell {...props} />
  ) : isPccReadmitted ? (
    <PccReadmittedCell {...props} />
  ) : (
    <DefaultPreflightCell {...props} />
  );
};

const DefaultPreflightCell = ({admission}: Props) => {
  const {updated, code} = admission;
  const updatedAt = toDate(updated * 1000);

  return (
    <DashboardCell>
      <WorkflowRibbon
        workflowType={admission.workflowType}
        star={
          <StarAdmissionIcon
            admissionId={admission.id}
            isStarred={admission.starred}
          />
        }
        actions={<PccAdmittedActions admission={admission} />}
      />
      <Row>
        <CellIdentityBlock>
          <ResidentName admission={admission} />
          <AdmissionId code={code} />
        </CellIdentityBlock>
      </Row>
      <AdmissionChecklist admission={admission} />
      <CellFooter>
        <Left>
          <CellLastUpdateRow>
            <CellLastUpdatePrefix>Last update:</CellLastUpdatePrefix>
            <CellLastUpdateTime>
              <RelativeDateTime
                dateTime={updatedAt}
                dateFormat={DATE_FORMAT.DAY_IN_MONTH}
              />
            </CellLastUpdateTime>
          </CellLastUpdateRow>
        </Left>
        <Right>
          <EditPreflightFooterButton admission={admission} />
        </Right>
      </CellFooter>
    </DashboardCell>
  );
};

const PccAdmittedCell = ({admission}: Props) => {
  const {code, updated} = admission;
  const updatedAt = toDate(updated * 1000);
  return (
    <DashboardCell>
      <WorkflowRibbon
        workflowType={admission.workflowType}
        star={
          <StarAdmissionIcon
            admissionId={admission.id}
            isStarred={admission.starred}
          />
        }
        actions={<PccAdmittedActions admission={admission} />}
      />
      <Row>
        <CellIdentityBlock>
          <ResidentName admission={admission} />
          <AdmissionId code={code} />
        </CellIdentityBlock>
      </Row>
      <div>
        <AdmissionChecklist admission={admission} />
        <SmallTag color="yellow100">New</SmallTag>
      </div>
      <CellFooter>
        <Left>
          <CellLastUpdateRow>
            <CellLastUpdatePrefix>Last update:</CellLastUpdatePrefix>
            <CellLastUpdateTime>
              <RelativeDateTime
                dateTime={updatedAt}
                dateFormat={DATE_FORMAT.DAY_IN_MONTH}
              />
            </CellLastUpdateTime>
          </CellLastUpdateRow>
        </Left>
        <Right>
          <EditPreflightFooterButton admission={admission} />
        </Right>
      </CellFooter>
    </DashboardCell>
  );
};

const PccReadmittedCell = ({admission}: Props) => {
  const {
    updated,
    code,
    pcc: {relatedAdmissionStatus},
  } = admission;
  const updatedAt = toDate(updated * 1000);
  return (
    <DashboardCell>
      <WorkflowRibbon
        workflowType={admission.workflowType}
        star={
          <StarAdmissionIcon
            admissionId={admission.id}
            isStarred={admission.starred}
          />
        }
        actions={<PccReadmittedActions admission={admission} />}
      />
      <Row>
        <CellIdentityBlock>
          <ResidentName admission={admission} />
          <AdmissionId code={code} />
        </CellIdentityBlock>
      </Row>
      <Paragraph>
        This resident has been re-admitted in PointClickCare.{" "}
        {relatedAdmissionStatus
          ? `There is an
        existing admission in RESIDE with status ${readableAdmissionStatus(
          relatedAdmissionStatus,
        )}.`
          : "There seems to be no existing admission in RESIDE."}
      </Paragraph>
      <CellFooter>
        <Left>
          <CellLastUpdateRow>
            <CellLastUpdatePrefix>Last update:</CellLastUpdatePrefix>
            <CellLastUpdateTime>
              <RelativeDateTime
                dateTime={updatedAt}
                dateFormat={DATE_FORMAT.DAY_IN_MONTH}
              />
            </CellLastUpdateTime>
          </CellLastUpdateRow>
        </Left>
        <Right>
          <EditPreflightFooterButton admission={admission} />
        </Right>
      </CellFooter>
    </DashboardCell>
  );
};

const ResidentName = ({admission}: Props) => (
  <CellResidentNameLink
    name={getAdmissionResidentName(admission)}
    data-test-id={getResidentTestId(admission, "noAction")}
    style={{display: "flex"}}
  />
);

const Row = styled.div`
  display: flex;
`;
